import React, { useState, useRef, useCallback, useEffect } from "react";
import "./ai-chatbot.css";
import apiService from "../../api/apiService";
import tiger0 from "../../images/tiger0.png";

const IS_ADMIN = true;

const DOMAINS = [
  {
    value: "messages",
    label: "Messages",
    description: "Explore conversations, channels, user activity and message trends",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="10" r="1" fill="currentColor" />
        <circle cx="12" cy="10" r="1" fill="currentColor" />
        <circle cx="15" cy="10" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "pr",
    label: "Purchase Requisitions",
    description: "Analyze PR costs, approval status and procurement data",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
        <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="9" y1="17" x2="12" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

const DOMAIN_PROMPTS = {
  messages: [
    { icon: "💬", title: "Active Today", prompt: "How many active conversations are there today?" },
    { icon: "👥", title: "Top Users", prompt: "Who are the most active users this week?" },
    { icon: "📊", title: "Channel Stats", prompt: "Show me message volume per channel this week" },
    { icon: "📈", title: "Trends", prompt: "What are the messaging trends this month?" },
  ],
  pr: [
    { icon: "💰", title: "Highest Amount", prompt: "What is the reference number of the most high amount?" },
    { icon: "⏳", title: "Pending Cost", prompt: "What is the total cost of all pending PRs this month?" },
    { icon: "📋", title: "PR Summary", prompt: "Give me a summary of all purchase requisitions this month" },
    { icon: "🔍", title: "Recent PRs", prompt: "Show me the most recent purchase requisitions" },
  ],
};

const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2);

const parseLine = (line) =>
  line
    .split(/\*\*(.*?)\*\*/g)
    .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

const renderContent = (text) =>
  text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {parseLine(line)}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));

function AiChatbot({ onClose, userDetails }) {
  const [domain, setDomain] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const streamTimerRef = useRef(null);
  const historyRef = useRef([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    };
  }, []);

  const streamResponse = useCallback((fullText, messageId) => {
    const words = fullText.split(" ");
    let i = 0;
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    streamTimerRef.current = setInterval(() => {
      i++;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, content: words.slice(0, i).join(" ") } : m,
        ),
      );
      if (i >= words.length) {
        clearInterval(streamTimerRef.current);
        setStreamingId(null);
        setIsTyping(false);
      }
    }, 35);
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || isTyping) return;
      const userMsgId = generateId();
      const aiMsgId = generateId();
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", content: text, ts: new Date() },
      ]);
      setIsTyping(true);

      try {
        const result = await apiService.talk_to_ai.talktoAI({
          question: text,
          history: historyRef.current,
          domain,
        });

        const answer = result?.answer ?? "Sorry, I couldn't get a response.";

        if (result?.success) {
          historyRef.current = [
            ...historyRef.current,
            { question: text, sqlQuery: result.sqlQuery ?? "", answer },
          ];
        }

        setMessages((prev) => [
          ...prev,
          { id: aiMsgId, role: "ai", content: "", ts: new Date() },
        ]);
        setStreamingId(aiMsgId);
        streamResponse(answer, aiMsgId);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: aiMsgId,
            role: "ai",
            content: "Something went wrong. Please try again.",
            ts: new Date(),
          },
        ]);
        setIsTyping(false);
      }
    },
    [isTyping, streamResponse, domain],
  );

  if (!IS_ADMIN) return null;

  const handleSelectDomain = (value) => setDomain(value);

  const handleChangeDomain = () => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    setDomain(null);
    setMessages([]);
    setIsTyping(false);
    setStreamingId(null);
    setInputValue("");
    historyRef.current = [];
  };

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      sendMessage(trimmed);
      setInputValue("");
    }
  };

  const handlePromptClick = (prompt) => sendMessage(prompt);

  const handleClearChat = () => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    setMessages([]);
    setIsTyping(false);
    setStreamingId(null);
    setInputValue("");
    historyRef.current = [];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const formatTime = (date) =>
    date.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });

  const activeDomain = DOMAINS.find((d) => d.value === domain);
  const suggestedPrompts = domain ? DOMAIN_PROMPTS[domain] : [];

  return (
    <div className="ai-chatbot-panel">
      {/* HEADER */}
      <div className="ai-chatbot__header">
        <div className="ai-chatbot__header-bot-icon">
          <img src={tiger0} alt="Tiger X AI" className="sidebar-header__nav-icon" />
        </div>
        <div className="ai-chatbot__header-info">
          <span className="ai-chatbot__header-title">Tiger X AI</span>
          <span className="ai-chatbot__header-subtitle">Admin Assistant</span>
        </div>

        {activeDomain && (
          <button
            className="ai-chatbot__domain-chip"
            onClick={handleChangeDomain}
            title="Switch domain"
          >
            <span className="ai-chatbot__domain-chip-label">{activeDomain.label}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m11 0h3a2 2 0 0 0 2-2v-3"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className="ai-chatbot__header-actions">
          {messages.length > 0 && (
            <button className="ai-chatbot__action-btn" onClick={handleClearChat} title="Clear conversation">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          <button className="ai-chatbot__action-btn ai-chatbot__close-btn" onClick={onClose} title="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* DOMAIN PICKER */}
      {!domain ? (
        <div className="ai-chatbot__domain-picker">
          <div className="ai-chatbot__domain-picker-icon-wrap">
            <div className="ai-chatbot__welcome-icon-ring" />
            <img src={tiger0} alt="Tiger X" className="ai-chatbot__welcome-icon sidebar-header__nav-icon-lg" />
          </div>
          <h2 className="ai-chatbot__domain-picker-title">Choose a Domain</h2>
          <p className="ai-chatbot__domain-picker-sub">
            What would you like to ask about today?
          </p>
          <div className="ai-chatbot__domain-cards">
            {DOMAINS.map((d) => (
              <button
                key={d.value}
                className={`ai-chatbot__domain-card ai-chatbot__domain-card--${d.value}`}
                onClick={() => handleSelectDomain(d.value)}
              >
                <div className="ai-chatbot__domain-card-icon">{d.icon}</div>
                <div className="ai-chatbot__domain-card-body">
                  <span className="ai-chatbot__domain-card-title">{d.label}</span>
                  <span className="ai-chatbot__domain-card-desc">{d.description}</span>
                </div>
                <svg className="ai-chatbot__domain-card-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* MESSAGES AREA */}
          <div className="ai-chatbot__messages">
            {messages.length === 0 ? (
              <div className="ai-chatbot__welcome">
                <div className="ai-chatbot__welcome-icon-wrap">
                  <div className="ai-chatbot__welcome-icon-ring" />
                  <img src={tiger0} alt="Tiger X" className="ai-chatbot__welcome-icon sidebar-header__nav-icon-lg" />
                </div>
                <h2 className="ai-chatbot__welcome-title">Tiger X AI Assistant</h2>
                <p className="ai-chatbot__welcome-subtitle">
                  Asking about <strong>{activeDomain.label}</strong>. Try one of these or type your own question.
                </p>
                <div className="ai-chatbot__prompts-grid">
                  {suggestedPrompts.map((p, i) => (
                    <button key={i} className="ai-chatbot__prompt-card" onClick={() => handlePromptClick(p.prompt)}>
                      <span className="ai-chatbot__prompt-icon">{p.icon}</span>
                      <span className="ai-chatbot__prompt-title">{p.title}</span>
                      <span className="ai-chatbot__prompt-text">{p.prompt}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ai-chatbot__message-list">
                {messages.map((msg) => (
                  <div key={msg.id} className={`ai-chatbot__message ai-chatbot__message--${msg.role}`}>
                    {msg.role === "ai" && (
                      <div className="ai-chatbot__avatar">
                        <img src={tiger0} alt="Tiger X" className="sidebar-header__nav-icon" />
                      </div>
                    )}
                    <div className="ai-chatbot__bubble-wrap">
                      <div className={`ai-chatbot__bubble ai-chatbot__bubble--${msg.role}`}>
                        {msg.role === "ai" && msg.content === "" ? (
                          <div className="ai-chatbot__typing"><span /><span /><span /></div>
                        ) : (
                          <div className="ai-chatbot__bubble-content">
                            {renderContent(msg.content)}
                            {msg.id === streamingId && <span className="ai-chatbot__cursor">▋</span>}
                          </div>
                        )}
                      </div>
                      <span className="ai-chatbot__ts">{formatTime(msg.ts)}</span>
                    </div>
                  </div>
                ))}
                {isTyping && streamingId === null && (
                  <div className="ai-chatbot__message ai-chatbot__message--ai">
                    <div className="ai-chatbot__avatar">
                      <img src={tiger0} alt="Tiger X" className="sidebar-header__nav-icon" />
                    </div>
                    <div className="ai-chatbot__bubble-wrap">
                      <div className="ai-chatbot__bubble ai-chatbot__bubble--ai">
                        <div className="ai-chatbot__typing"><span /><span /><span /></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* INPUT AREA */}
          <div className="ai-chatbot__input-area">
            <div className="ai-chatbot__input-wrap">
              <textarea
                ref={inputRef}
                className="ai-chatbot__input"
                placeholder={isTyping ? "Tiger X AI is responding…" : `Ask about ${activeDomain.label}…`}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={isTyping}
              />
              <button
                className={`ai-chatbot__send-btn${!inputValue.trim() || isTyping ? " ai-chatbot__send-btn--off" : ""}`}
                onClick={handleSend}
                disabled={!inputValue.trim() || isTyping}
                title="Send message"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AiChatbot;
